/// <reference types="@types/applepayjs" />

/**
 * Wallet billing and shipping addresses → the backend's `AddressCreate` (PAY-1953).
 *
 * Apple Pay, Google Pay and Link skip the checkout form, so the sheet's billing
 * address is the only way the customer's zip reaches the customer page, and the
 * sheet's shipping address (when the merchant asks for one) the only way a
 * shipping address does. Each wallet names the fields its own way; this is the
 * one place they are renamed.
 */

/** Mirrors the backend's `AddressCreate`. */
export type AddressPayload = {
  line1?: string;
  line2?: string;
  line3?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
};

/** Stripe's address shape (`billing_details.address`), as Stripe.js returns it. */
export type StripeAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
};

/** Trim a value; an empty or blank one becomes `undefined`, never `""`. */
export function clean(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function nonEmpty(address: AddressPayload): AddressPayload | undefined {
  return Object.values(address).some((v) => v !== undefined) ? address : undefined;
}

/** Stripe says `postal_code`; we say `zip_code`. Undefined when nothing is filled. */
export function fromStripeAddress(address: StripeAddress | null | undefined): AddressPayload | undefined {
  if (!address) return undefined;
  return nonEmpty({
    line1: clean(address.line1),
    line2: clean(address.line2),
    city: clean(address.city),
    state: clean(address.state),
    zip_code: clean(address.postal_code),
    country: clean(address.country),
  });
}

/** Apple Pay's `billingContact` (requested with `postalAddress`). Undefined when nothing is filled. */
export function fromApplePayContact(
  contact: ApplePayJS.ApplePayPaymentContact | undefined,
): AddressPayload | undefined {
  if (!contact) return undefined;
  return nonEmpty({
    line1: clean(contact.addressLines?.[0]),
    line2: clean(contact.addressLines?.[1]),
    line3: clean(contact.addressLines?.slice(2).join(", ")),
    city: clean(contact.locality),
    state: clean(contact.administrativeArea),
    zip_code: clean(contact.postalCode),
    // countryCode is ISO alpha-2; `country` is free text the backend normalizes.
    country: clean(contact.countryCode) ?? clean(contact.country),
  });
}

/**
 * Stripe's `PaymentRequestShippingAddress` (the `paymentmethod` event's `shippingAddress`,
 * present when the request set `requestShipping`). Undefined when nothing is filled.
 */
export type StripeShippingAddress = {
  addressLine?: string[] | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

export function fromStripeShippingAddress(
  address: StripeShippingAddress | null | undefined,
): AddressPayload | undefined {
  if (!address) return undefined;
  return nonEmpty({
    line1: clean(address.addressLine?.[0]),
    line2: clean(address.addressLine?.[1]),
    line3: clean(address.addressLine?.slice(2).join(", ")),
    city: clean(address.city),
    state: clean(address.region),
    zip_code: clean(address.postalCode),
    country: clean(address.country),
  });
}

/** The Google Pay API's `Address` (`paymentData.shippingAddress`). Undefined when nothing is filled. */
export type GooglePayAddress = {
  address1?: string;
  address2?: string;
  address3?: string;
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
  countryCode?: string;
};

export function fromGooglePayAddress(address: GooglePayAddress | null | undefined): AddressPayload | undefined {
  if (!address) return undefined;
  return nonEmpty({
    line1: clean(address.address1),
    line2: clean(address.address2),
    line3: clean(address.address3),
    city: clean(address.locality),
    state: clean(address.administrativeArea),
    zip_code: clean(address.postalCode),
    country: clean(address.countryCode),
  });
}

/** The `shipping_*` fields a merchant form passes to `submit`. */
export type ShippingFields = {
  shipping_address_line1?: string;
  shipping_address_line2?: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_zip_code?: string;
  shipping_country?: string;
};

/**
 * A shipping address the shopper typed into the merchant's form. Sent as
 * `customer_info.shipping_address`, where it wins over the wallet sheet's.
 *
 * Undefined unless a street line is filled: a form that only pre-selects a
 * country (or is half done) has not given an address. Counting it would stop
 * the sheet from asking and then override the sheet's full address with a
 * fragment.
 */
export function shippingAddressFromFields(fields: ShippingFields | null | undefined): AddressPayload | undefined {
  if (!fields || !clean(fields.shipping_address_line1)) return undefined;
  return nonEmpty({
    line1: clean(fields.shipping_address_line1),
    line2: clean(fields.shipping_address_line2),
    city: clean(fields.shipping_city),
    state: clean(fields.shipping_state),
    zip_code: clean(fields.shipping_zip_code),
    country: clean(fields.shipping_country),
  });
}
